/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'reactstrap';
import * as default_data from '../config.js';
import { getToken } from '../AuthService.js';
import { TableContainer, Button, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Paper } from '@mui/material';
import AdminHorizontalMenu from './AdminHorizontalMenu.js';
import { FaFileCsv } from 'react-icons/fa'; // Import the CSV file icon
import { getBannerColor } from '../Utils/classification_helper.js';

const TokenStats = ({ type }) => {
  const [months, setMonths] = useState([]);
  const [loaded, setLoaded] = useState(false);
  var loaded_static = false;

    const [isExporting, setIsExporting] = useState(false); // State to track export status
  
    // Function to handle CSV export
    const handleExportUsers = async (full = false) => {
      const token = getToken();
      if (!token) {
        console.error('Token not found');
        return;
      }
  
      setIsExporting(true); // Set exporting state to true

      const dataToSerialize = { full: full }; // Example of a simple object

      try {
        const response = await fetch(default_data.default_user_service_url_no_cac + '/' + type + '-export-users-csv', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-access-tokens': `${token}`
          },
          body: JSON.stringify(dataToSerialize)
        });
  
        if (!response.ok) {
          throw new Error('An error occurred while exporting users.');
        }
  
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'users.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (error) {
        console.error(error);
      } finally {
        setIsExporting(false); // Reset exporting state
      }
    };
  
  function loadStats() {
    var token = getToken();
    if (token === false) {
      return;
    }

    fetch(default_data.default_user_service_url_no_cac + '/' + type + '-get-token-stats', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({})
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while getting data.');
      })
      .then(data => {
        if (data.status != 200) {
          window.location = '/login';
          return;
        }
        data = data.response;
        setMonths(data.tokens_count);
      })
      .catch(error => {
        console.log(error);
      });
  }

  useEffect(() => {
    if (loaded || loaded_static) {
      return;
    }

    setLoaded(true);
    loaded_static = true;

    loadStats();
  }, []);

  const DisplayData = months.map((info) => {
    return (
      <TableRow key={info.date}>
        <TableCell>{info.date}</TableCell>
        <TableCell>
          {navigator.language.includes("en") || navigator.language.includes("US") || navigator.language.includes("us")
            ? info.count_inference.toLocaleString(navigator.language)
            : info.count_inference}
        </TableCell>
        <TableCell>
          {navigator.language.includes("en") || navigator.language.includes("US") || navigator.language.includes("us")
            ? info.count_train.toLocaleString(navigator.language)
            : info.count_train}
        </TableCell>
      </TableRow>
    );
  });

  return (
    <div>
      {
        <div className={`header-message ${getBannerColor(true)}`}>
          {default_data.default_banner_override != null && default_data.default_banner_override != '' ?
            default_data.default_banner_override : 'CUI'
          }
        </div>
      }
      <div className="reg-page-mode">
        <Container>
          <Row className="justify-content-center reg-page-mode-header">
            <Col xs="12" md="12" lg="12" className="d-flex align-items-center">
              <a href="/" className="d-flex align-items-center">
                <img src="/logo.png" alt="Ask Sage" className="logo" />
              </a>
              <h1 className="user-list-h1">
                Token Stats
              </h1>
            </Col>
          </Row>
          <Row className="user-list-reg-page-mode-content justify-content-center reg-page-mode-content">
            <Col xs="12" md="12" lg="12">
              <AdminHorizontalMenu type={type} full_stats={0} />
              <TableContainer component={Paper}>
                <Table className="table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Tokens Count Inference</TableCell>
                      <TableCell>Tokens Count Training</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {DisplayData.length === 0 ?
                      <TableRow><TableCell colSpan="3">Loading...</TableCell></TableRow> : ''}
                    {DisplayData}
                  </TableBody>
                </Table>
              </TableContainer>

              <Button
                style={{ margin: '10px', width: '150px' }}
                variant="contained"
                size="small"
                onClick={() => handleExportUsers(true)}
                startIcon={isExporting ? <CircularProgress size={20} /> : <FaFileCsv />} // Show spinner if exporting
                disabled={isExporting} // Disable button if exporting
              >
                {isExporting ? 'Exporting...' : 'Export Users'}
              </Button>

              {type === 'admin' && (
                  <Button
                  style={{ margin: '10px', width: '200px' }}
                  variant="contained"
                  size="small"
                  onClick={() => handleExportUsers(false)}
                  startIcon={isExporting ? <CircularProgress size={20} /> : <FaFileCsv />} // Show spinner if exporting
                  disabled={isExporting} // Disable button if exporting
                >
                  {isExporting ? 'Exporting...' : 'Export Users with Tokens'}
                </Button>
                )}
            </Col>
          </Row>
          <Row className="justify-content-center reg-page-mode-footer">
            <Col xs="12" md="12" lg="12">
              Copyright &copy; {default_data.default_copywrite_year} Ask Sage, Inc.
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export { TokenStats };